import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CONSTANTS } from 'src/app/config';
import { LocalService } from './local.service';
import { AcknowledgementService } from './acknowledgement.service';
import { DashboardService } from './dashboard.service';
import { StoreService } from './store.service';
import { Subject } from 'rxjs';
import { filterUserRoles, isPamUser, isCompanyBlocked, isEmearBlocked, isOnlyApprover, hasApproverRole, hasCiscoAdminRole } from 'src/app/shared/utils/user.util';

@Injectable({
    providedIn: 'root',
})
export class CommonRouteService {
    acknowledgement$ = new Subject<any>();

    constructor(
        private router: Router,
        private acknowledgementService: AcknowledgementService,
        readonly dashboardService: DashboardService,
        private storeService: StoreService
    ) { }

    navigateUser(userData: any, ignoreNavigation: boolean): any {
        if (userData.company === null && userData.id === null && userData.userType !== 'PAM') {
            this.router.navigateByUrl('/user-access-validation');
            return false;
        }
        if ((userData.company !== null && userData.id === null) && userData.pvieligible === false && !(userData.userType === 'PAM')) {
            this.router.navigateByUrl('/enroll');
            return false;
        }
        if (isCompanyBlocked(userData)) {
            this.router.navigateByUrl('/blocked');
            return false;
        }
        if (userData.grsUser && userData.onlyRedeemPoints && !userData.active) {
            this.router.navigateByUrl('/redeem-points');
            return false;
        }
        if (userData.onlyRedeemPoints) {
            this.router.navigateByUrl('/redeem-points');
            return false;
        }
        if (userData.userType === 'PAM' && !userData.pamDefaultSelection.beGeoIds) {
            this.router.navigateByUrl('/user-access-validation');
            return false;
        }
        const approver = LocalService.get('onlyApprover');
        const ciscoAdminRole = LocalService.get('ciscoAdminRole');
        const approverRole = LocalService.get('approverRole');
        let managerFlag: boolean;
        let sellerFlag: boolean;
        if (userData.active && userData.company.profileComplete) {
            managerFlag = false;
            sellerFlag = false;
            if (isPamUser(userData)) {
                managerFlag = true;
            } else {
                managerFlag = filterUserRoles(userData, CONSTANTS.manager).length > 0;
                sellerFlag = filterUserRoles(userData, CONSTANTS.seller).length > 0;
            }
            if (ignoreNavigation) {
                return true;
            }
            if (ciscoAdminRole && approverRole && !isPamUser(userData) && managerFlag) {
                this.router.navigateByUrl('/home/manager/manage-challenges');
                return;
            }
            if (managerFlag) {
                this.router.navigateByUrl('/home/manager/manage-challenges');
                this.getAcknowledgements('LEADER');
                return;
            }
            if (sellerFlag) {
                this.router.navigateByUrl('/home/seller/challenges');
                this.getAcknowledgements('SELLER');
                return;
            }
            if (approver) {
                this.router.navigateByUrl('/approver');
                return;
            }
        } else if (userData.hasOwnProperty('userType') && (userData.userType === 'PAM') && userData.active === false) {
            if (userData.pamActivity.createProfleClicked) {
                this.router.navigateByUrl('/profile');
                return false;
            }
            this.router.navigateByUrl('/welcome');
            return false;
        } else if (!userData.id && userData.userEnrolled && userData.pvieligible) {
            this.router.navigateByUrl('/profile');
            return false;
        } else if (userData.adminInPss && !userData.company.profileComplete && !userData.company.companyProfileInformationProvided) {
            this.router.navigateByUrl('/profile');
            return false;
        } else if (!userData.company.profileComplete) {
            this.router.navigateByUrl('/user-access-validation');
            return false;
        } else if ((userData.roles && userData.roles.length) || (userData.pendingRoles && userData.pendingRoles.length)) {
            this.router.navigateByUrl('/user-access-validation');
            return false;
        }
        this.router.navigateByUrl('/profile');
        return false;
    }

    getAcknowledgements(role: string): void {
        this.acknowledgementService.getAcknowledgements(role).subscribe((response: any) => {
            if (response && response.length > 0) {
                this.acknowledgement$.next(response);
            }
        });
    }

    async setupUserData(userData: any): Promise<boolean> {
        localStorage.setItem('selectedLanguage', 'English');
        if (isEmearBlocked(userData)) {
            this.router.navigateByUrl('/blocked');
            return true;
        }
        if (userData.hasOwnProperty('userType') && userData['userType'] === 'PAM') {
            const regData = await this.getPartnerInfoData(userData);
            const countryList = await this.getcountryList(userData, regData);
            const beGeoData = await this.getBGeoId(userData, countryList);
            const pamDefaultSelection: any = {};
            pamDefaultSelection['countryGroup'] = countryList['countryGroups'][0];
            pamDefaultSelection['pamBeGeoIdsDTO'] = { beGeoIds: beGeoData };
            pamDefaultSelection['theater'] = regData['theaters'][0];
            LocalService.set('pamData', pamDefaultSelection);
            if (userData['pamDefaultSelection']['beGeoIds'] && userData['pamDefaultSelection']['beGeoIds'].length > 0) {
                if (userData.company !== null) {
                    userData.company['id'] = userData['pamDefaultSelection']['beGeoIds'][0].companyId;
                    userData['company']['region'] = userData['pamDefaultSelection']['beGeoIds'][0].region;
                } else {
                    userData.company = { region: userData['pamDefaultSelection']['beGeoIds'][0].region };
                }
            }
            LocalService.set('userProfile', userData);
        }
        if (isOnlyApprover(userData)) {
            LocalService.flush('approverRole');
            LocalService.set('onlyApprover', true);
        } else {
            LocalService.flush('onlyApprover');
            if (hasApproverRole(userData)) {
                const role = { approverRole: true };
                LocalService.set('approverRole', role);
            }
        }
        if (hasCiscoAdminRole(userData)) {
            LocalService.set('ciscoAdminRole', true);
        }
        this.storeService.updateRole.next(true);
        return false;
    }

    async navigateUserAfterLogin(userData: any): Promise<void> {
        const alreadyNavigated = await this.setupUserData(userData);
        if (alreadyNavigated) {
            return;
        }
        this.navigateUser(userData, false);
    }

    getPartnerInfoData(val: any): Promise<any> {
        const promise = new Promise((resolve, reject) => {
            this.dashboardService.getRegionList(val.username).subscribe(
                (data: any) => { resolve(data); },
                (error: any) => { reject(error); }
            );
        });
        return promise;
    }

    getcountryList(userData: any, reg: any): Promise<any> {
        const promise = new Promise((resolve, reject) => {
            this.dashboardService.getCountryListRequest(userData.username, reg['theaters'][0]).subscribe(
                (data: any) => { resolve(data); },
                (error: any) => { reject(error); }
            );
        });
        return promise;
    }

    getBGeoId(userData: any, countryGrp: any): Promise<any> {
        const paramReq: any = {};
        paramReq.username = userData.username;
        paramReq.countries = [countryGrp['countryGroups'][0]];
        const promise = new Promise((resolve, reject) => {
            this.dashboardService.getBeGeoIdsRequest(paramReq).subscribe(
                (data: any) => { resolve(data); },
                (error: any) => { reject(error); }
            );
        });
        return promise;
    }

}
